import type { IncidentStatus } from "./api";
import { DEFAULT_SORT, type IncidentSort } from "./list-sort";
import type { IncidentListSearch } from "./search";
import { seedStatuses, statusesParam } from "./status-filter";

/**
 * The incident list's view - every filter, the search term and the sort - and
 * the URL it lives in ([F-011]).
 *
 * The list used to hold its filters in component state, which a visit to an
 * incident unmounted: coming back started from an unfiltered list in the
 * default order. The view now IS the URL's search (the build feasibility
 * check's reading of AC-012: "filter and sort live in the URL"), so the
 * browser's back button, a reload and a shared link show the same list.
 *
 * The in-app way back - the detail page's "Zurück zur Liste" - has no history
 * entry to return to, so the list also remembers the search it last showed
 * for this tab, and that link goes there.
 */

/** "No filter" in the toolbar Selects (Radix Select has no empty value). */
export const ALL = "all";

/** The filters that come only from a deep link and show as removable chips. */
export type DeepFilters = Pick<
  IncidentListSearch,
  "categoryId" | "createdAfter" | "missingPostmortem" | "overdueFollowup"
>;

export interface IncidentListView {
  q: string;
  statusSel: IncidentStatus[];
  severityFilter: string;
  areaFilter: string;
  tagFilter: string;
  ownerFilter: string;
  discoveredFromDay: string;
  discoveredToDay: string;
  mineOnly: boolean;
  withArchive: boolean;
  deepFilters: DeepFilters;
  sort: IncidentSort;
}

export function viewFromSearch(search: IncidentListSearch): IncidentListView {
  return {
    q: search.q ?? "",
    statusSel: seedStatuses(search),
    severityFilter: search.severityId ?? ALL,
    areaFilter: search.areaId ?? ALL,
    tagFilter: search.tagId ?? ALL,
    ownerFilter: search.ownerId ?? ALL,
    discoveredFromDay: search.from ?? "",
    discoveredToDay: search.to ?? "",
    mineOnly: search.mine === true,
    withArchive: search.archived === "include",
    deepFilters: {
      ...(search.categoryId ? { categoryId: search.categoryId } : {}),
      ...(search.createdAfter ? { createdAfter: search.createdAfter } : {}),
      ...(search.missingPostmortem ? { missingPostmortem: true } : {}),
      ...(search.overdueFollowup ? { overdueFollowup: true } : {}),
    },
    sort: search.sort ? { field: search.sort, dir: search.dir ?? "asc" } : DEFAULT_SORT,
  };
}

/**
 * The canonical search for a view: every default left out, so an unfiltered
 * list is a bare /incidents and two equal views are one URL. The status
 * selection always travels as `statuses`.
 */
export function searchFromView(view: IncidentListView): IncidentListSearch {
  const q = view.q.trim();
  const statuses = statusesParam(view.statusSel);
  const defaultSort = view.sort.field === DEFAULT_SORT.field && view.sort.dir === DEFAULT_SORT.dir;
  return {
    ...(q ? { q } : {}),
    ...(statuses ? { statuses } : {}),
    ...(view.severityFilter !== ALL ? { severityId: view.severityFilter } : {}),
    ...(view.areaFilter !== ALL ? { areaId: view.areaFilter } : {}),
    ...(view.tagFilter !== ALL ? { tagId: view.tagFilter } : {}),
    ...(view.ownerFilter !== ALL ? { ownerId: view.ownerFilter } : {}),
    ...(view.discoveredFromDay ? { from: view.discoveredFromDay } : {}),
    ...(view.discoveredToDay ? { to: view.discoveredToDay } : {}),
    ...(view.mineOnly ? { mine: true } : {}),
    ...(view.withArchive ? { archived: "include" as const } : {}),
    ...view.deepFilters,
    ...(defaultSort ? {} : { sort: view.sort.field, dir: view.sort.dir }),
  };
}

let lastSearch: IncidentListSearch = {};

/** Remember the search the list shows, for the in-app way back. */
export function rememberListSearch(search: IncidentListSearch): void {
  lastSearch = search;
}

/** The search the list last showed in this tab ({} before it was opened). */
export function lastListSearch(): IncidentListSearch {
  return lastSearch;
}
